'use client';

import {
  FocusEvent,
  forwardRef,
  ForwardedRef,
  KeyboardEvent,
  ReactNode,
  useEffect,
  useId,
  useState,
} from 'react';

import { Label } from '../Label';
import classNames from 'classnames';

import './css/Switch.scss';

export interface SwitchProps {
  className?: string;
  disabled?: boolean;
  isChecked?: boolean;
  label: ReactNode;
  onClick?: () => void;
  onBlur?: (e: FocusEvent<HTMLInputElement>) => void;
  onFocus?: (e: FocusEvent<HTMLInputElement>) => void;
  onKeyUp?: (e: KeyboardEvent<HTMLInputElement>) => void;
  switchId?: string;
}

export const Switch = forwardRef(
  (props: SwitchProps, ref: ForwardedRef<HTMLDivElement>) => {
    const customSwitchId = useId();

    const {
      className,
      disabled,
      isChecked = false,
      label,
      onClick,
      switchId = customSwitchId,
      onBlur: propOnBlur,
      onFocus: propOnFocus,
      onKeyUp: propOnKeyUp,
      ...spreadProps
    } = props;

    const [isSwitchChecked, setIsSwitchChecked] = useState(isChecked);

    const labelId = `${switchId}-label`;

    const switchClasses = classNames({
      'ps-switch': true,
      'ps-switch-disabled': disabled,
      'ps-switch-checked': isSwitchChecked,
      [`${className}`]: className,
    });

    const handleChecked = () => {
      setIsSwitchChecked(!isSwitchChecked);

      if (onClick) return onClick();
    };

    const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
      if (disabled) return;

      if (e.code === 'Space' || e.code === 'Enter') {
        // Prevents the key press from scrolling the page
        e.preventDefault();

        setIsSwitchChecked(!isSwitchChecked);
      }

      if (propOnKeyUp) propOnKeyUp(e);
    };

    useEffect(() => {
      setIsSwitchChecked(isChecked);
    }, [isChecked]);

    return (
      <div
        {...spreadProps}
        className={switchClasses}
        onClick={handleChecked}
        onKeyDown={handleKeyDown}
        ref={ref}
      >
        <Label
          disabled={disabled}
          isBold={false}
          id={labelId}
        >
          {label}
        </Label>

        <div
          aria-checked={isSwitchChecked ? 'true' : 'false'}
          aria-disabled={disabled}
          aria-labelledby={labelId}
          className="ps-switch-button"
          id={switchId}
          onBlur={propOnBlur}
          onFocus={propOnFocus}
          role="switch"
          tabIndex={disabled ? undefined : 0}
        >
          <div className="ps-switch-track"></div>
          <div className="ps-switch-thumb">
            {isSwitchChecked ? (
              <svg
                width="10"
                height="8"
                viewBox="0 0 10 8"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M3.66972 8C3.48624 8 3.30275 7.89333 3.21101 7.78667L0 4.26667L1.00917 3.09333L3.66972 6.08L9.08257 0L10 1.17333L4.0367 7.89333C3.94495 8 3.85321 8 3.66972 8Z"
                  fill="currentColor"
                />
              </svg>
            ) : (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="8"
                height="8"
                fill="none"
              >
                <path
                  stroke="currentColor"
                  d="M7 .843 1 6.907M1 .843l6 6.064"
                />
              </svg>
            )}
          </div>
        </div>
      </div>
    );
  }
);
